import { FC, PropsWithChildren } from 'react';
import Head from 'next/head';
import { FileVariantsEnum, makeEventPageLink, makeHostPageLink } from '@sweatpals/shared';
import { DEEP_LINK_PROTOCOL, PUBLIC_FILE_PATH, HOME_REDIRECT_URL } from '../../constants';
import { FEATURE_CUSTOM_SMART_BANNER_ON_IOS } from 'constants/features';
import { EventResponseType, EventType } from '../../services/types/event-types';
import { UserDataType } from '../../services/types/user-types';
import { getPublicFileUrl } from '../../utils/file.utils';
import { removeMarkdown } from 'utils/removeMarkdown';

export interface OGMetaProps {
	pageTitle?: string;
	title?: string;
	siteName?: string;
	description?: string;
	keywords?: string[];
	image?: string;
	url?: string;
	twitterCardType?: string;
	twitterLabel1?: string;
	twitterData1?: string;
	locale?: string;
	ogType?: string;
	deepLink?: string;
	hideSmartBanner?: boolean;
}

// eslint-disable-next-line complexity
const OGMeta: FC<PropsWithChildren<OGMetaProps>> = ({
	pageTitle,
	title,
	keywords,
	siteName,
	description,
	image,
	url,
	twitterCardType = 'summary_large_image',
	twitterLabel1,
	twitterData1,
	locale = 'en_US',
	ogType = 'website',
	children,
	deepLink,
	hideSmartBanner,
}) => (
	<>
		<Head>
			{pageTitle && <title>{pageTitle}</title>}

			{/* basic */}
			{title && <meta name='title' key='title' content={title} />}
			{description && <meta name='description' key='description' content={description} />}
			{keywords && keywords.length > 0 && <meta name='keywords' key='keywords' content={keywords.join(', ')} />}

			{/* open graph */}
			{title && <meta property='og:title' key='og:title' content={title} />}
			{ogType && <meta property='og:type' key='og:type' content={ogType} />}
			{locale && <meta property='og:locale' key='og:locale' content={locale} />}
			{siteName && <meta property='og:site_name' key='og:site_name' content={siteName} />}
			{description && <meta property='og:description' key='og:description' content={description} />}
			{image && <meta name='image' property='og:image' key='og:image' content={image} />}
			{image && <meta name='image' property='og:image:url' key='og:image:url' content={image} />}
			{image && <meta name='image' property='og:image:secure_url' key='og:image:secure_url' content={image} />}
			{url && <meta property='og:url' key='og:url' content={url} />}

			{/* twitter */}
			{title && <meta property='twitter:title' key='twitter:title' content={title} />}
			{twitterCardType && <meta name='twitter:card' key='twitter:card' content={twitterCardType} />}
			{description && <meta property='twitter:description' key='twitter:description' content={description} />}
			{twitterLabel1 && <meta name='twitter:label1' key='twitter:label1' content={twitterLabel1} />}
			{twitterData1 && <meta name='twitter:data1' key='twitter:data1' content={twitterData1} />}
			{image && <meta name='twitter:image' key='twitter:image' content={image} />}
			{url && <meta property='twitter:url' key='twitter:url' content={url} />}

			{/* pinterest */}
			{image && <meta name='pinterest:image' key='pinterest:image' content={image} />}
			{description && <meta name='pinterest:description' key='pinterest:description' content={description} />}

			{!FEATURE_CUSTOM_SMART_BANNER_ON_IOS && !hideSmartBanner && (
				<>
					<meta
						name='apple-itunes-app'
						key='apple-itunes-app'
						content={`app-id=1595991156, app-argument=${deepLink ?? `${DEEP_LINK_PROTOCOL}://app`}`}
					/>
					<meta name='smartbanner:hide-ttl' key='smartbanner:hide-ttl' content='1000' />
				</>
			)}

			{/* other */}
			{children}
		</Head>
	</>
);

export default OGMeta;

export const getEventSEOMeta = (event: EventType, eventInstances?: EventType[]): OGMetaProps => {
	const eventPageUrl = `${HOME_REDIRECT_URL}/${makeEventPageLink({
		eventAlias: event?.alias,
		shortLocalInstance: eventInstances?.length > 1 ? event?.shortLocalInstance : undefined,
	})}`;
	const appEventUrl = `${DEEP_LINK_PROTOCOL}://${makeEventPageLink({
		eventAlias: event?.alias,
		shortLocalInstance: eventInstances?.length > 1 ? event?.shortLocalInstance : undefined,
	})}`;

	const eventDescription = event?.description?.length > 200
		? `${removeMarkdown(event?.description).slice(0, 200)}...`
		: event?.description;

	const seo: OGMetaProps = {
		pageTitle: 'SweatPals - Whatever your ability, this is your community',
		description: eventDescription,
		title: event?.author?.fullName ? `Join ${event?.name} by ${event?.author?.fullName}` : `Join ${event?.name}`,
		image: event?.background ? getPublicFileUrl(event?.background, { width: 100, quality: 75, variant: FileVariantsEnum.M }) : '/assets/icons/logo.svg',
		url: eventPageUrl,
		deepLink: appEventUrl,
	};

	return seo;
};

export const getHostSEOMeta = (host?: UserDataType): OGMetaProps => {
	const hostPageUrl = `${HOME_REDIRECT_URL}/${makeHostPageLink({ userName: host?.userName })}`;
	const appHostLink = `${DEEP_LINK_PROTOCOL}://${makeHostPageLink({ userName: host?.userName })}`;

	const seo: OGMetaProps = {
		pageTitle: 'SweatPals - Whatever your ability, this is your community',
		description: 'SweatPals app connects you to your local fitness & wellness events and communities!',
		title: `Join ${host?.fullName} on Sweatpals`,
		image: host?.backgroundId
			? getPublicFileUrl(host?.backgroundId, {
					quality: 75,
					width: 100,
					variant: FileVariantsEnum.M,
			  })
			: host?.avatarId
			? getPublicFileUrl(host?.avatarId, {
					quality: 75,
					width: 100,
					variant: FileVariantsEnum.M,
			  })
			: '/assets/icons/logo.svg',
		url: hostPageUrl,
		deepLink: appHostLink,
	};

	return seo;
};
